// src/lib/theme/colors.ts
// ============================================================================
// THE DECK COMPASS: CANONICAL COLOUR SYSTEM
// Version: 1.0 (Brand Colors Canonical V1.0)
// Status: LOCKED - Do not modify without versioned update
// ============================================================================

export const colors = {
  // ========================================================================
  // PRIMARY PALETTE (System Colours)
  // ========================================================================

  /**
   * Paper / Canvas
   * Primary background surface. Foundational "paper" colour establishing
   * calm, editorial aesthetic.
   * Usage: Main canvas, cards, primary button backgrounds
   */
  paper: '#F3F0EA',

  /**
   * Compass Ink / Authority
   * Primary text and dark contrast colour. Authoritative "ink" for body text,
   * headings, button text, icons. Never pure black.
   * Usage: Body text, headings, titles, button text, icons
   */
  ink: '#1F2A2E',

  /**
   * Muted Ocean / Presence
   * Secondary text and link colour. Calming, ocean-inspired tone for
   * metadata and secondary information.
   * Usage: Metadata, secondary info, calm interactive text
   */
  muted: '#3F5C65',

  /**
   * Sacred Gold / Bands & Lines
   * Accent colour for structure and focus. Restrained, sacred, never dominant.
   * Usage: Hairline borders (40%), dividers, focus rings (100%)
   */
  gold: '#C6A14A',

  /**
   * Off-white / Light Text
   * Lighter variant of Paper for subtle contrast and hover states.
   * Usage: Hover backgrounds, light elevation, light contrast, light text on dark
   */
  offWhite: '#F7F4EF',

  /**
   * Active Dark (Pressed State)
   * Slightly darker paper tone for active/pressed button states.
   * Extracted from Card.tsx interactive active state.
   */
  activeDark: '#EFEAE3',

  // ========================================================================
  // CONTEXTUAL ACCENT COLOURS (Editorial Use Only)
  // NEVER use for UI buttons, links, or system feedback
  // ========================================================================

  /**
   * Sky Reflection
   * Contextual accent for clarity, reflection, openness.
   * Usage: Editorial markers, small glyphs, illustration accents (max 60% opacity)
   * RESTRICTION: Never adjacent to Muted Ocean in same component
   */
  skyReflection: '#74B3CE',

  /**
   * Burnt Rose
   * Contextual accent for depth, emotion, alchemical warmth.
   * Usage: Rare editorial emphasis, symbolic markers (max 50% opacity)
   * RESTRICTION: Never paired with Sky Reflection or Sacred Gold in high-contrast ways
   */
  burntRose: '#90323D',

  // ========================================================================
  // SEMANTIC ALIASES (For code clarity)
  // ========================================================================

  background: '#F3F0EA', // Paper
  text: {
    primary: '#1F2A2E', // Compass Ink
    secondary: '#3F5C65', // Muted Ocean
  },
  border: '#C6A14A', // Sacred Gold
  accent: '#C6A14A', // Sacred Gold

  // ========================================================================
  // DISABLED & REDUCED STATES
  // ========================================================================

  disabled: {
    background: '#F3F0EA', // Paper, reduced opacity via tokens
    text: '#1F2A2E', // Ink, reduced to 50-60% opacity via tokens
  },
} as const;

export type ColorKey = keyof typeof colors;
export type Colors = typeof colors;